import * as React from "react";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CircularProgress from "@mui/material/CircularProgress";
import CNVRow from "./ExpandableCNVRow";

// Placeholder columns configuration
const columns = [
  { field: "gene_symbol", headerName: "Gene", flex: 0.3 },
  { field: "cnv_type", headerName: "Type", flex: 0.2 },
  { field: "exons", headerName: "Exons", flex: 0.3 },
  { field: "size", headerName: "Size (bp)", flex: 0.2 },
];

// Placeholder data for demonstration
const placeholderData = [
  {
    id: 1,
    gene_symbol: "BRCA1",
    cnv_type: "Deletion",
    exons: "Exons 5-8",
    start_exon: 5,
    end_exon: 8,
    total_exons: 23,
    size: 12500,
    chromosome: "chr17",
    start_position: 43044295,
    end_position: 43056795,
    copy_number: 1,
    quality_score: 95.5,
    description: "Heterozygous deletion affecting tumor suppressor gene",
    gnomad_sv_af: 0.0001,
    clinical_significance: "Pathogenic",
  },
  {
    id: 2,
    gene_symbol: "TP53",
    cnv_type: "Duplication",
    exons: "Exon 3",
    start_exon: 3,
    end_exon: 3,
    total_exons: 11,
    size: 3200,
    chromosome: "chr17",
    start_position: 7676154,
    end_position: 7679354,
    copy_number: 3,
    quality_score: 88.2,
    description: "Partial duplication in tumor suppressor gene",
    gnomad_sv_af: null,
    clinical_significance: "Uncertain Significance",
  },
  {
    id: 3,
    gene_symbol: "PTEN",
    cnv_type: "Deletion",
    exons: "Exons 1-9",
    start_exon: 1,
    end_exon: 9,
    total_exons: 9,
    size: 105000,
    chromosome: "chr10",
    start_position: 87863113,
    end_position: 87968113,
    copy_number: 1,
    quality_score: 92.7,
    description: "Complete gene deletion",
    gnomad_sv_af: 0.00005,
    clinical_significance: "Pathogenic",
  },
  {
    id: 4,
    gene_symbol: "EGFR",
    cnv_type: "Amplification",
    exons: "Exons 12-28",
    start_exon: 12,
    end_exon: 28,
    total_exons: 28,
    size: 89400,
    chromosome: "chr7",
    start_position: 55087058,
    end_position: 55176458,
    copy_number: 8,
    quality_score: 97.3,
    description: "High-level amplification common in glioblastoma",
    gnomad_sv_af: null,
    clinical_significance: "Likely Pathogenic",
  },
  {
    id: 5,
    gene_symbol: "RB1",
    cnv_type: "Deletion",
    exons: "Exons 15-19",
    start_exon: 15,
    end_exon: 19,
    total_exons: 27,
    size: 28700,
    chromosome: "chr13",
    start_position: 48303751,
    end_position: 48332451,
    copy_number: 1,
    quality_score: 90.1,
    description: "Deletion in retinoblastoma tumor suppressor",
    gnomad_sv_af: 0.00002,
    clinical_significance: "Pathogenic",
  },
  {
    id: 6,
    gene_symbol: "MYC",
    cnv_type: "Amplification",
    exons: "Exons 1-3",
    start_exon: 1,
    end_exon: 3,
    total_exons: 3,
    size: 7500,
    chromosome: "chr8",
    start_position: 127735434,
    end_position: 127742934,
    copy_number: 12,
    quality_score: 94.8,
    description: "Oncogene amplification",
    gnomad_sv_af: null,
    clinical_significance: "Pathogenic",
  },
];

function NoRowsOverlay() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <SearchOffIcon sx={{ fontSize: 60, color: "text.secondary", mb: 1 }} />
      <Typography variant="h6" color="text.secondary">
        No CNV variants found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Try adjusting your filters or search criteria
      </Typography>
    </Box>
  );
}

function CNVList(props) {
  const [pageSize, setPageSize] = React.useState(25);
  const [isLoading, setIsLoading] = React.useState(true);
  const apiRef = useGridApiRef();

  // Simulate initial data loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#1a4774ff" }} />
        <Typography variant="h6" sx={{ mt: 2, color: "#1a4774ff" }}>
          Loading CNV Data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: placeholderData.length === 0 ? "300px" : "1000px",
        width: "100%",
        position: "relative",
      }}
    >
      <Box sx={{ display: "flex", height: "100%" }}>
        <Box sx={{ flexGrow: 1, position: "relative" }}>
          <DataGrid
            rowCount={placeholderData.length}
            apiRef={apiRef}
            columns={columns}
            pagination
            paginationMode="client"
            rows={placeholderData}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              Row: CNVRow,
              Header: () => null,
              NoRowsOverlay: NoRowsOverlay,
            }}
            componentsProps={{
              row: {
                project_type: props.project_type,
                bam_files: props.bam_files,
                apiRef: apiRef,
              },
            }}
            disableSelectionOnClick
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
                display: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                marginTop: "0 !important",
              },
            }}
            disableVirtualization
          />
        </Box>
      </Box>
    </Box>
  );
}

export default CNVList;
